import React, { useEffect, useState } from "react"
import { FaArrowRight } from "react-icons/fa"

import { useRecoilValue } from "recoil"

import { stripeUrlState } from "@fuse/recoil/stripe"

import letshangLogo from "@fuse/assets/icons/lets-hang.svg"
import messengerLogo from "@fuse/assets/icons/messenger.svg"
import stripeLogo from "@fuse/assets/icons/stripe.svg"

interface StripeConnectionModalProps {
  isOpen: boolean
  onClose: () => void
  onConnectToStripe: () => Promise<void>
}

const StripeConnectionModal: React.FC<StripeConnectionModalProps> = ({
  isOpen,
  onClose,
  onConnectToStripe,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const stripeUrl = useRecoilValue(stripeUrlState)

  useEffect(() => {
    if (!stripeUrl.isLoading && stripeUrl.url && isLoading) {
      setIsLoading(false)
      onConnectToStripe()
    }
  }, [stripeUrl, isLoading])

  const handleConnectToStripe = async () => {
    if (stripeUrl.url && !stripeUrl.isLoading) {
      onConnectToStripe()
    } else {
      setIsLoading(true)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={onClose}
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="w-full md:max-w-md bg-[#252525B2] backdrop-blur-xl rounded-t-[32px] rounded-b-none p-8 pt-6 flex flex-col items-center shadow-2xl animate-slideUp"
        style={{
          borderTopLeftRadius: 32,
          borderTopRightRadius: 32,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle for mobile look */}
        <div className="w-16 h-2 rounded-full bg-white/20 opacity-60 mb-4 mt-1" />
        <h2 className="text-white text-2xl font-bold text-center mb-2">
          Connect to Stripe
        </h2>
        <div className="flex flex-col gap-4 w-full">
          <div className="flex justify-center items-center gap-4 w-full my-4">
            <img
              src={letshangLogo}
              alt="Let's Hang Logo"
              className="w-20 h-20 rounded-full object-cover border-2 border-white shadow-lg bg-black"
            />
            <span className="text-3xl text-white/80">
              <img
                src={messengerLogo}
                alt="Messenger Logo"
                className="w-8 aspect-square rounded-full object-contain"
              />
            </span>
            <img
              src={stripeLogo}
              alt="Stripe Logo"
              className="w-20 h-20 rounded-full object-contain border-2 border-white shadow-lg bg-[#635bff]"
            />
          </div>
          <p className="text-white/50 text-center mb-8 text-base font-medium">
            Start selling tickets to your events by creating a Stripe account.
            It usually takes less than 5 minutes to set up.
          </p>
        </div>
        <button
          onClick={handleConnectToStripe}
          disabled={isLoading}
          className={`w-full bg-white px-4 py-3 rounded-2xl text-lg flex items-center justify-center gap-2 shadow-md transition ${
            isLoading
              ? "opacity-50 cursor-not-allowed pointer-events-none"
              : "hover:bg-gray-100"
          }`}
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 m-1 border-2 border-gray-600 border-t-transparent rounded-full animate-spin mr-2" />
            </>
          ) : (
            <>
              Connect to Stripe
              <FaArrowRight className="ml-1 rotate-[-45deg]" />
            </>
          )}
        </button>
      </div>
      <style>{`
        .animate-slideUp {
          animation: slideUpSheet 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideUpSheet {
          from {
            transform: translateY(100%);
            opacity: 0.7;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  )
}

export default StripeConnectionModal
